import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Send01Icon } from '@untitledui-icons/react/line'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { FormInput, FormSelect } from '@/components/forms'
import {
  useInviteUser,
  useLocales,
  getReadableLocaleName,
} from '@/hooks/api/useUserManagement'

// Validation schema
const userInviteSchema = z.object({
  email: z.string().email('app.validation.invalid_email'),
  first_name: z.string().min(1, 'app.validation.first_name_required'),
  last_name: z.string().min(1, 'app.validation.last_name_required'),
  locale: z.string().min(1, 'app.validation.language_required'),
})

type UserInviteFormValues = z.infer<typeof userInviteSchema>

export function UserInvitePage() {
  const { t } = useTranslation()
  const { customerId, partnerId } = useParams<{
    customerId?: string
    partnerId?: string
  }>()
  const navigate = useNavigate()

  // Build base path for navigation
  const basePath = customerId
    ? `/customers/${customerId}/users`
    : partnerId
      ? `/partners/${partnerId}/users`
      : '/users'

  // Fetch locales
  const { data: locales = [] } = useLocales()

  // Mutation
  const inviteUser = useInviteUser()

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<UserInviteFormValues>({
    resolver: zodResolver(userInviteSchema),
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
      locale: locales[0] || 'en',
    },
  })

  // Locale options
  const localeOptions = locales.map((locale) => ({
    value: locale,
    label: getReadableLocaleName(locale),
  }))

  // Handle form submission
  const onSubmit = async (data: UserInviteFormValues) => {
    try {
      await inviteUser.mutateAsync({
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        locale: data.locale,
        customer_id: customerId ? parseInt(customerId, 10) : undefined,
        partner_id: partnerId ? parseInt(partnerId, 10) : undefined,
      })
      toast.success(t('resources.users.invite_sent_success'))
      navigate(basePath)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('resources.users.create_error')
      toast.error(message)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('resources.users.invite_title')}
        subtitle={t('resources.users.invite_subtitle')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-6">
          {/* User Info */}
          <div className="space-y-4">
            <FormInput
              control={control}
              name="email"
              label={t('resources.users.fields.email')}
              type="email"
              placeholder={t('resources.users.placeholders.email')}
              isRequired
            />

            <FormInput
              control={control}
              name="first_name"
              label={t('resources.users.fields.first_name')}
              placeholder={t('resources.users.placeholders.first_name')}
              isRequired
            />

            <FormInput
              control={control}
              name="last_name"
              label={t('resources.users.fields.last_name')}
              placeholder={t('resources.users.placeholders.last_name')}
              isRequired
            />
          </div>

          {/* Settings */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <FormSelect
              control={control}
              name="locale"
              label={t('resources.users.fields.locale')}
              options={localeOptions}
              isRequired
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
            <Button color="secondary" type="button" onPress={() => navigate(basePath)}>
              {t('resources.generic.cancel')}
            </Button>
            <Button color="primary" type="submit" isDisabled={isSubmitting} iconLeading={Send01Icon}>
              {isSubmitting ? t('resources.users.sending') : t('resources.users.send_invite')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default UserInvitePage
